import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PaperPlaneTilt, Clock, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import usePageMeta from "@/lib/usePageMeta";
import QuietModeGuard from "@/components/QuietModeGuard";

export default function Messages() {
  usePageMeta({ title: "Messages", description: "Private, disappearing conversations with your Wingwomen peers, 24 hours, 7 days, or forever." });
  return <QuietModeGuard><MessagesInner /></QuietModeGuard>;
}

function MessagesInner() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [ttl, setTtl] = useState("24h");
  const [loading, setLoading] = useState(true);

  const loadThreads = async () => {
    const r = await api.get("/messages/threads");
    setThreads(r.data.threads); setLoading(false);
  };
  useEffect(() => { loadThreads(); }, []);

  const openThread = async (t) => {
    setSelected(t);
    const r = await api.get("/messages/thread", { params: { peer_id: t.peer_id } });
    setThread(r.data.messages);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !selected) return;
    try {
      await api.post("/messages", { to_user_id: selected.peer_id, body, ttl });
      setBody("");
      const r = await api.get("/messages/thread", { params: { peer_id: selected.peer_id } });
      setThread(r.data.messages);
      loadThreads();
    } catch { toast.error("Could not send"); }
  };

  return (
    <div>
      <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-3"><span className="rule" />Private messages</div>
      <h1 className="font-display text-4xl leading-tight">Disappearing conversations.</h1>
      <p className="mt-3 text-ink/70 max-w-xl">Send with a 24-hour or 7-day timer, or keep forever. Your call.</p>

      <div className="mt-8 grid md:grid-cols-12 gap-6">
        <div className="md:col-span-4 bg-white border border-border rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-border text-xs tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
            <Users size={14} weight="duotone" /> Threads
          </div>
          <div className="max-h-[520px] overflow-y-auto wing-scroll" data-testid="threads-list">
            {loading ? <div className="p-6 text-muted-foreground text-sm">Loading…</div>
              : threads.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground leading-relaxed">
                  No conversations yet. Reach out from a match card to start one.
                </div>
              ) : threads.map(t => (
                <button key={t.thread_key} onClick={() => openThread(t)}
                        data-testid={`thread-${t.peer_anon}`}
                        className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted transition-colors ${
                          selected?.thread_key === t.thread_key ? "bg-coral-50" : ""
                        }`}>
                  <div className="font-display text-base">{t.peer_anon}</div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{t.last_body}</div>
                  <div className="text-[10px] tracking-[0.15em] uppercase text-sage-600 mt-1 flex items-center gap-1">
                    <Clock size={10} /> {t.last_ttl}
                  </div>
                </button>
              ))}
          </div>
        </div>

        <div className="md:col-span-8 bg-white border border-border rounded-2xl overflow-hidden flex flex-col h-[560px]">
          {!selected ? (
            <div className="flex-1 grid place-items-center text-muted-foreground text-sm">
              Select a thread to start chatting.
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="font-display text-lg">{selected.peer_anon}</div>
                <select value={ttl} onChange={(e) => setTtl(e.target.value)}
                        data-testid="ttl-select"
                        className="text-xs bg-muted border border-border rounded-full px-3 py-1.5">
                  <option value="24h">24-hour disappearing</option>
                  <option value="7d">7-day disappearing</option>
                  <option value="permanent">Keep forever</option>
                </select>
              </div>
              <div className="flex-1 overflow-y-auto p-6 wing-scroll space-y-4" data-testid="dm-scroll">
                {thread.map((m) => {
                  const mine = m.to_id === selected.peer_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                        mine ? "bg-coral text-white rounded-br-md" : "bg-sage-50 border border-sage-100 rounded-bl-md"
                      }`}>
                        <div className="whitespace-pre-wrap">{m.body}</div>
                        <div className={`text-[10px] mt-1.5 tracking-widest uppercase ${
                          mine ? "text-white/70" : "text-sage-600"
                        }`}>{m.ttl}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="border-t border-border p-3 flex items-center gap-2 bg-cream">
                <input value={body} onChange={(e) => setBody(e.target.value)}
                       placeholder="Type your message…"
                       data-testid="dm-input"
                       className="flex-1 bg-white border border-border rounded-full px-4 py-2.5 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none" />
                <button type="submit" data-testid="dm-send"
                        className="bg-coral text-white p-2.5 rounded-full hover:bg-coral-600 transition-colors">
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
